import { ApiError, authHeader, type ApiErrorBody } from './client';

export interface ShapeTabStat { name: string; read: number; husRemoved: number; kept: number }
export interface ShapeStats { perTab: ShapeTabStat[]; plusSkipped: number; outputRows: number }
export interface ShapeResult { filename: string; blob: Blob; stats: ShapeStats }

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Upload a raw quarterly korttitoimitus .xlsx; the API returns the reshaped
 * workbook as a BINARY body (blob), with filename + stats in response headers.
 * Bypasses the JSON `api()` helper on the success path; error bodies are still
 * the standard JSON shape and surface as ApiError.
 */
export async function shapeKorttitoimitus(file: File): Promise<ShapeResult> {
  const headers = new Headers({
    'content-type': XLSX_MIME,
    // Header values must be Latin-1 — encode so exotic filenames can't throw.
    'x-taksikortti-filename': encodeURIComponent(file.name),
  });
  const auth = await authHeader();
  if (auth) headers.set('authorization', auth);

  const res = await fetch('/api/korttitoimitus/shape', { method: 'POST', headers, body: file });

  if (!res.ok) {
    let payload: ApiErrorBody = { code: `http_${res.status}` };
    try { payload = (await res.json()) as ApiErrorBody; } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, payload);
  }

  const blob = await res.blob();
  const fnHeader = res.headers.get('x-taksikortti-filename');
  const filename = fnHeader ? decodeURIComponent(fnHeader) : 'korttitoimitus_valmis.xlsx';
  let stats: ShapeStats = { perTab: [], plusSkipped: 0, outputRows: 0 };
  const statsHeader = res.headers.get('x-taksikortti-stats');
  if (statsHeader) {
    try { stats = JSON.parse(decodeURIComponent(statsHeader)) as ShapeStats; } catch { /* keep defaults */ }
  }
  return { filename, blob, stats };
}
